import React, { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import {
  Card,
  Typography,
  Switch,
  Row,
  Col,
  Tag,
  Spin,
  message,
  Button,
  Modal,
} from "antd"
import PanelLayout from "../../Layout/PanelLayout"
import apiClient from "../../Utils/apiClient"
import {
  ShieldCross,
  ShieldSecurity,
  ShieldSlash,
  ShieldTick,
} from "iconsax-react"
import dayjs from "dayjs"
import BitLockerKeys from "../../components/BitLockerKeys/BitLockerKeys"
import {
  getBitlockerProtectionLabel,
  getBitlockerVolumeStatusLabel,
  isBitlockerProtectionOn,
} from "../../Utils/bitlockerStatus"

const { Title } = Typography

const formatSignatureUpdated = (value) => {
  if (value == null || value === "") return "N/A"
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return dayjs(value).format("DD MMM YY, hh:mm A")
  }
  const text = String(value).trim()
  if (!text) return "N/A"
  const dotNet = text.match(/\/Date\((-?\d+)\)/)
  const digits = dotNet?.[1] || text.match(/-?\d{10,}/)?.[0]
  const parsed = digits
    ? dayjs(Number(digits))
    : dayjs(text)
  return parsed.isValid() ? parsed.format("DD MMM YY, hh:mm A") : "N/A"
}

const SecurityTabs = () => {
  const { tab, hostname } = useParams()

  const [bitlockerStatuses, setBitlockerStatuses] = useState([])
  const [recoveryKeys, setRecoveryKeys] = useState([])
  const [defender, setDefender] = useState({})
  const [usbStatus, setUsbStatus] = useState("Unknown")
  const [loading, setLoading] = useState(true)
  const [switchUsbLoading, setSwitchUsbLoading] = useState(false)
  const [switchBitLoading, setSwitchBitLoading] = useState(false)
  const [switchWipe, setSwitchWipe] = useState(false)

  useEffect(() => {
    getData()
  }, [tab, hostname])

  const getData = async () => {
    setLoading(true)
    try {
      const data = await apiClient(`devices/host/${hostname}`, "GET")

      const currentTab = String(tab || "").toLowerCase()
      if (currentTab === "usb") {
        setUsbStatus(data?.usbStatus || "Unknown")
      } else if (currentTab === "bitlocker") {
        setBitlockerStatuses(data?.bitlockerStatus || [])
        setRecoveryKeys(data?.bitlockerRecoveryKeys || [])
      } else if (currentTab === "defender") {
        setDefender(data?.defender || {})
      }
    } catch (error) {
      message.error("Failed to load device data")
    } finally {
      setLoading(false)
    }
  }
  const handleToggleBitLocker = async (mountPoint, currentStatus) => {
    const enabled = isBitlockerProtectionOn(currentStatus)
    try {
      setSwitchBitLoading(true)
      const command = enabled
        ? `bitlocker-disable:${mountPoint}`
        : `bitlocker-enable:${mountPoint}`
      const payload = { hostname: hostname, command }

      const response = await apiClient(
        `devices/${hostname}/create-task`,
        "POST",
        payload
      )

      if (response.success) {
        message.success(
          `BitLocker on drive ${mountPoint} ${
            enabled ? "disabled" : "enabled"
          }. Recovery keys are stored when enable completes.`
        )
        setBitlockerStatuses((prev) =>
          prev.map((item) =>
            item.MountPoint === mountPoint
              ? { ...item, ProtectionStatus: enabled ? 0 : 1 }
              : item
          )
        )
      } else {
        throw new Error(response.message || "Failed to update BitLocker")
      }
    } catch (error) {
      message.error(
        error.message ||
          `Something went wrong while updating BitLocker for drive ${mountPoint}`
      )
    } finally {
      setSwitchBitLoading(false)
    }
  }
  const handleToggleUSB = async () => {
    try {
      setSwitchUsbLoading(true)
      const newStatus = usbStatus === "Enabled" ? "Disabled" : "Enabled"
      const command = usbStatus === "Enabled" ? "usb-disable" : "usb-enable"
      const payload = { command, hostname }

      const response = await apiClient(
        `devices/${hostname}/task`,
        "POST",
        payload
      )

      if (response.success) {
        message.success(`USB access ${newStatus}`)
        setUsbStatus(newStatus)
      } else {
        message.warning(response.message || "Failed to update USB access")
      }
    } catch (error) {
      message.error("Error updating USB status")
    } finally {
      setSwitchUsbLoading(false)
    }
  }

  const handleWipe = async () => {
    try {
      setSwitchWipe(true)
      const command = "wipe"
      const payload = { command, hostname: hostname }
      message.info(`Start Wipe: ${hostname} has started`)
      console.log("payload =============>", payload)

      const response = await apiClient(
        `devices/${hostname}/task`,
        "POST",
        payload
      )

      if (response.success) {
        message.success(`Wipe is completed for ${hostname}`)
      } else {
        message.warning(response.message || "Failed to Wipe")
      }
    } catch (error) {
      message.info(error.message || "Something went wrong while Wipe Device")
    } finally {
      setSwitchWipe(false)
    }
  }

  const showConfirm = () => {
    Modal.confirm({
      title: "Are you sure you want to wipe this device?",
      content:
        "This erases user data on the device. BitLocker recovery keys are saved to Device Manager first when they exist. This cannot be undone.",
      okText: "Yes, Wipe",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        handleWipe()
      },
    })
  }
  const renderBoolean = (value) => {
    if (value === true) return <ShieldTick color="#52c41a" size={18} />
    if (value === false) return <ShieldCross color="#ff4d4f" size={18} />
    return <ShieldSlash color="#d9d9d9" size={18} />
  }
  const currentTab = String(tab || "").toLowerCase()
  return (
    <PanelLayout
      title={`${currentTab.toUpperCase()} data for ${hostname}`}
      subtitle="Security inventory details for this endpoint."
    >
      <div>
        <Spin spinning={loading}>
          {currentTab === "bitlocker" && (
            <div className="mt-3 w-100">
              <Row gutter={[16, 16]}>
                {bitlockerStatuses.map((bitlocker) => (
                  <Col
                    key={bitlocker.MountPoint}
                    xs={24}
                    sm={24}
                    md={12}
                    lg={8}
                  >
                    <Card
                      title={`BitLocker - Drive ${bitlocker.MountPoint}`}
                      extra={
                        <Switch
                          checked={isBitlockerProtectionOn(
                            bitlocker.ProtectionStatus
                          )}
                          loading={switchBitLoading}
                          disabled={switchBitLoading}
                          onChange={() =>
                            handleToggleBitLocker(
                              bitlocker.MountPoint,
                              bitlocker.ProtectionStatus
                            )
                          }
                        />
                      }
                      bordered
                    >
                      <p>Drive : {bitlocker?.MountPoint}</p>
                      <p>
                        Volume Status:{" "}
                        <Tag color="blue">
                          {getBitlockerVolumeStatusLabel(bitlocker.VolumeStatus)}
                        </Tag>
                      </p>
                      <p>
                        Status:{" "}
                        <Tag
                          color={
                            isBitlockerProtectionOn(bitlocker.ProtectionStatus)
                              ? "green"
                              : "red"
                          }
                        >
                          {getBitlockerProtectionLabel(
                            bitlocker.ProtectionStatus,
                            bitlocker.VolumeStatus
                          )}
                        </Tag>
                      </p>
                      <BitLockerKeys
                        keys={recoveryKeys}
                        mountPoint={bitlocker.MountPoint}
                      />
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          )}

          {currentTab === "usb" && (
            <div className="mt-3 w-100">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={24} md={12} lg={8}>
                  <Card
                    title="USB Access Control"
                    extra={
                      <Switch
                        checked={usbStatus === "Enabled"}
                        loading={switchUsbLoading}
                        onChange={handleToggleUSB}
                      />
                    }
                    bordered
                  >
                    <Title level={5} style={{ fontSize: "16px" }}>
                      USB Status:{" "}
                      <Tag color={usbStatus === "Enabled" ? "green" : "red"}>
                        {usbStatus}
                      </Tag>
                    </Title>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
          {currentTab === "wipe" && (
            <div className="mt-3 w-100">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={24} md={12} lg={8}>
                  <Card
                    title="Wipe Controle"
                    extra={
                      <>
                        <Button onClick={showConfirm} className="my-2">
                          Wipe
                        </Button>
                      </>
                    }
                    bordered
                  >
                    <Row gutter={16} align="middle">
                      <Col span={24}>
                        <div>
                          <span
                            style={{ fontSize: "16px", fontWeight: "bold" }}
                          >
                            Wipe Device:{" "}
                          </span>
                          <span>
                            Securely erase all data from the device, ensuring
                            that personal files, applications, and settings are
                            permanently removed and the device is restored to
                            its factory state.
                          </span>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
          {currentTab === "defender" && (
            <div className="mt-3 w-100">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={24} md={24} lg={24}>
                  <Card
                    title={`Defender`}
                    extra={
                      <>
                        <ShieldSecurity color="#52c41a" size={25} />
                      </>
                    }
                    bordered
                  >
                    <Row gutter={16} align="start">
                      <Col span={24}>
                        <p>
                          <strong>AM Service Enabled : </strong>
                          {renderBoolean(defender?.AMServiceEnabled)}
                        </p>
                        <p>
                          <strong>Real Time Protection Enabled: </strong>
                          {renderBoolean(defender?.RealTimeProtectionEnabled)}
                        </p>
                        <p>
                          <strong>Antispyware Enabled: </strong>
                          {renderBoolean(defender?.AntispywareEnabled)}
                        </p>
                        <p>
                          <strong>Antivirus Enabled: </strong>
                          {renderBoolean(defender?.AntivirusEnabled)}
                        </p>
                        <p>
                          <strong>Behavior Monitor Enabled: </strong>
                          {renderBoolean(defender?.BehaviorMonitorEnabled)}
                        </p>
                        <p>
                          <strong>On Access Protection Enabled: </strong>
                          {renderBoolean(defender?.OnAccessProtectionEnabled)}
                        </p>
                        <p>
                          <strong>Ioav Protection Enabled: </strong>
                          {renderBoolean(defender?.IoavProtectionEnabled)}
                        </p>

                        <p>
                          <strong>NIS Enabled: </strong>
                          {renderBoolean(defender?.NISEnabled)}
                        </p>
                        <p>
                          <strong>Firewall Enabled: </strong>
                          {renderBoolean(defender?.FirewallEnabled)}
                        </p>
                        <p>
                          <strong>Antivirus Signature Version: </strong>
                          {defender?.AntivirusSignatureVersion || "N/A"}
                        </p>
                        <p>
                          <strong>Antivirus Signature LastUpdated: </strong>
                          {formatSignatureUpdated(
                            defender?.AntivirusSignatureLastUpdated
                          )}
                        </p>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
        </Spin>
      </div>
    </PanelLayout>
  )
}

export default SecurityTabs
